/**
 * Suggests a correction for a mistyped email domain (gmial.com -> gmail.com),
 * mailcheck-style: common mailbox domains and TLDs compared by edit distance.
 * Suggests, never blocks. TypeScript copy of the PHP signup form's
 * common/modules/user/assets/email-suggest/email-suggest.js; the Jest test
 * runs both against the same cases, so change the two together.
 */

const DOMAINS: readonly string[] = [
    'gmail.com', 'googlemail.com', 'hotmail.com', 'hotmail.co.uk', 'hotmail.fr', 'hotmail.de', 'hotmail.nl',
    'hotmail.it', 'hotmail.es', 'hotmail.se', 'outlook.com', 'outlook.de', 'outlook.fr', 'outlook.nl', 'live.com',
    'live.nl', 'live.fr', 'live.de', 'live.be', 'msn.com', 'yahoo.com', 'yahoo.co.uk', 'yahoo.fr', 'yahoo.de',
    'yahoo.es', 'yahoo.it', 'ymail.com', 'icloud.com', 'aol.com', 'gmx.de', 'gmx.net', 'gmx.at', 'gmx.ch',
    'web.de', 't-online.de', 'freenet.de', 'ziggo.nl', 'kpnmail.nl', 'hetnet.nl', 'telenet.be', 'skynet.be',
    'orange.fr', 'wanadoo.fr', 'laposte.net', 'libero.it', 'virgilio.it', 'tiscali.it', 'interia.pl',
    'bigpond.com', 'optusnet.com.au', 'protonmail.com', 'comcast.net', 'verizon.net', 'sbcglobal.net',
    'btinternet.com', 'virginmedia.com', 'bluewin.ch', 'seznam.cz',
    // Real domains one typo away from the ones above, listed so they are left alone
    'mail.com', 'email.com', 'me.com', 'mac.com', 'gmx.com', 'proton.me', 'hey.com', 'fastmail.com', 'zoho.com',
    'yandex.com', 'yandex.ru', 'mail.ru', 'inbox.com', 'hotmail.be', 'outlook.be', 'live.co.uk', 'live.it',
    'home.nl', 'planet.nl', 'xs4all.nl', 'casema.nl', 'upcmail.nl', 'chello.nl', 'online.nl', 'tele2.nl',
    'free.fr', 'sfr.fr', 'o2.pl', 'wp.pl', 'onet.pl', 'op.pl', 'telia.com', 'sky.com', 'att.net', 'cox.net',
    'alice.it', 'tin.it', 'gmx.fr', 'aim.com'
];
const TLDS: readonly string[] = ['com', 'net', 'org', 'de', 'nl', 'be', 'fr', 'it', 'es', 'pl', 'se', 'fi', 'at', 'ch', 'co.uk', 'com.au', 'cz', 'eu'];

/** Optimal string alignment distance: insertions, deletions, substitutions and adjacent swaps. */
export const editDistance = (a: string, b: string): number => {
    const d: number[][] = [];
    for (let i = 0; i <= a.length; i++) {
        d[i] = [i];
    }
    for (let j = 0; j <= b.length; j++) {
        d[0]![j] = j;
    }
    for (let i = 1; i <= a.length; i++) {
        for (let j = 1; j <= b.length; j++) {
            const cost = a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1;
            let value = Math.min(d[i - 1]![j]! + 1, d[i]![j - 1]! + 1, d[i - 1]![j - 1]! + cost);
            if (i > 1 && j > 1 && a.charAt(i - 1) === b.charAt(j - 2) && a.charAt(i - 2) === b.charAt(j - 1)) {
                value = Math.min(value, d[i - 2]![j - 2]! + 1);
            }
            d[i]![j] = value;
        }
    }
    return d[a.length]![b.length]!;
};

const closest = (value: string, list: readonly string[], maxFor: (candidate: string) => number): string | null => {
    let best: string | null = null;
    let bestDistance = Infinity;
    for (const candidate of list) {
        const dist = editDistance(value, candidate);
        if (dist < bestDistance && dist <= maxFor(candidate)) {
            best = candidate;
            bestDistance = dist;
        }
    }
    return best;
};

/** The corrected address, or null when the domain looks right (or unknown but plausible). */
export const suggestEmail = (input: string): string | null => {
    const email = String(input || '').trim();
    const at = email.lastIndexOf('@');
    if (at < 1 || at === email.length - 1) {
        return null;
    }
    const local = email.slice(0, at);
    const domain = email.slice(at + 1).toLowerCase();
    if (DOMAINS.includes(domain)) {
        return null;
    }
    let fix = closest(domain, DOMAINS, (candidate) => (candidate.length <= 7 ? 1 : 2));
    if (!fix) {
        // An unknown domain with a mistyped TLD: company.con -> company.com
        const dot = domain.indexOf('.');
        if (dot > 0) {
            const name = domain.slice(0, dot);
            const tld = domain.slice(dot + 1);
            if (!TLDS.includes(tld)) {
                const tldFix = closest(tld, TLDS, () => 1);
                fix = tldFix ? `${name}.${tldFix}` : null;
            }
        }
    }
    return fix && fix !== domain ? `${local}@${fix}` : null;
};
